'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn, hasValue } from '@woonstad/helpers-utils';
import { forwardRef } from 'react';

import { Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';

type RadioGroupProps = {
  children: RadioGroupPrimitive.RadioGroupProps['children'];
  horizontal?: boolean;
  defaultValue?: string;
  transformValue?: (value?: NonNullable<ReturnType<typeof useFormField>['field']>['value']) => any;
  transformValueOnChange?: (value: string) => any;
  onValueChange?: RadioGroupPrimitive.RadioGroupProps['onValueChange'];
};

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(({
  children,
  horizontal,
  defaultValue,
  transformValueOnChange,
  transformValue,
  onValueChange,
}, ref) => {
  const { field } = useFormField();
  const value = hasValue(transformValue) ? transformValue(field?.value) : field?.value;

  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      className={cn('gap-4', horizontal ? 'flex flex-wrap' : 'grid')}
      disabled={field?.disabled}
      value={value}
      onValueChange={(value) => {
        const transformedValue = hasValue(transformValueOnChange) ? transformValueOnChange(value) : value;

        if (onValueChange) {
          onValueChange(value);
        }

        field?.onChange(transformedValue);
      }}
      defaultValue={defaultValue}
      name={field?.name}
    >
      {children}
    </RadioGroupPrimitive.Root>
  );
});

RadioGroup.displayName = 'RadioGroup';

type RadioGroupLabelProps = {
  value: string;
  label: string;
  disabled?: boolean;
  name?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  testid?: string;
  id?: string; // Optional ID to overwrite the radix IDs for more control in parent componts
};

export const RadioGroupItem = ({
  value,
  label,
  disabled,
  onClick,
  name,
  testid,
  id,
}: RadioGroupLabelProps) => {
  const {
    field,
    fieldState,
    formItemId,
  } = useFormField();

  const isDisabled = disabled || field?.disabled;
  const elementId = id ?? `${formItemId}-${value}`;

  const inputClasses = cn(
    'flex size-5 min-w-5 max-w-5 items-center justify-center rounded-full border-2 outline-offset-4',
    fieldState.error && 'border-input-error',
    !isDisabled && 'border-radiobutton-enabled hover:cursor-pointer hover:border-radiobutton-hover',
    isDisabled && 'border-radiobutton-disabled',
  );

  return (
    <div
      className="flex items-center"
    >
      <RadioGroupPrimitive.Item
        aria-label={label}
        className={inputClasses}
        disabled={disabled || field?.disabled}
        value={value}
        onClick={onClick}
        data-testid={testid}
        id={elementId}
      >
        <RadioGroupPrimitive.Indicator
          className={cn('size-3 rounded-full bg-checkbox-enabled-toggle', disabled || field?.disabled && 'bg-checkbox-disabled-toggle')}
        />
      </RadioGroupPrimitive.Item>
      <Typography
        data-disabled={disabled || field?.disabled}
        htmlFor={elementId}
        textColorClassname="text-radiobutton-enabled hover:text-radiobutton-hover data-[disabled]:text-radiobutton-disabled"
        type="label"
        variant="content-body"
        className={cn('pl-2', {
          'cursor-pointer': !isDisabled,
        })}
      >
        {label}
      </Typography>
    </div>
  );
};
